import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { MessageSquare, Loader2, ArrowLeft } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { ForgotPasswordRequestSchema, authApi } from "@/lib/api";

export const Route = createFileRoute("/auth/forgot-password")({
  beforeLoad: () => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: ForgotPassword,
});

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  // Create forgot password mutation
  const forgotPasswordMutation = useMutation({
    mutationFn: authApi.forgotPassword,
    onSuccess: () => {
      setIsSuccess(true);
    },
    onError: (error: Error) => {
      setError(error.message || "An error occurred. Please try again.");
      console.error("Forgot password error:", error);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSuccess(false);

    try {
      // Validate form data with Zod schema
      const forgotPasswordData = ForgotPasswordRequestSchema.parse({ email });

      // Execute forgot password mutation
      forgotPasswordMutation.mutate(forgotPasswordData);
    } catch (err: any) {
      // Handle validation errors
      if (err.errors) {
        setError(err.errors.map((e: any) => e.message).join(", "));
      } else {
        setError("Please check your input and try again.");
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 relative max-w-lg mx-auto">
      {/* Background elements */}
      <div className="absolute top-[20%] right-[10%] w-64 h-64 bg-blue-300 rounded-full blur-3xl opacity-20 animate-float"></div>
      <div
        className="absolute bottom-[10%] left-[15%] w-72 h-72 bg-purple-300 rounded-full blur-3xl opacity-10 animate-float"
        style={{ animationDelay: "2s" }}
      ></div>

      <div className="w-full max-w-md animate-fade-up">
        <div className="mb-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center shadow-md">
              <MessageSquare className="h-6 w-6 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-3xl font-bold">
            aiEmailAgent<span className="text-blue-600">.</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            The AI-powered email assistant for sales teams
          </p>
        </div>

        <div className="glass rounded-xl p-8 shadow-lg border">
          <div className="space-y-2 mb-6">
            <h2 className="text-2xl font-bold">Reset Password</h2>
            <p className="text-muted-foreground text-sm">
              {isSuccess
                ? "Check your email for reset instructions"
                : "Enter your email to receive a password reset link"}
            </p>
          </div>

          {error && (
            <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive mb-4 animate-scale">
              {error}
            </div>
          )}

          {isSuccess ? (
            <div className="text-center space-y-6">
              <div className="rounded-md bg-green-50 p-4 mb-4">
                <p className="text-sm text-green-800">
                  If an account exists with that email, we've sent instructions
                  to reset your password.
                </p>
              </div>
              <Button
                onClick={() =>
                  navigate({
                    to: "/auth/login",
                    search: { redirectTo: "/auth/forgot-password" },
                  })
                }
                variant="outline"
                className="w-full"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="text-sm font-medium leading-none"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  required
                  disabled={forgotPasswordMutation.isPending}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary text-primary-foreground py-2.5 mt-2 hover:shadow-md transition-all duration-200 rounded-lg text-sm font-medium"
                disabled={forgotPasswordMutation.isPending}
              >
                {forgotPasswordMutation.isPending ? (
                  <span className="flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  "Send Reset Link"
                )}
              </Button>

              <div className="mt-4 text-center text-sm">
                <Link
                  to="/auth/login"
                  className="text-sm font-medium text-primary hover:underline"
                  search={{ redirectTo: "/auth/forgot-password" }}
                >
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
